import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Copy, Radio, Wifi, WifiOff } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = BACKEND.replace(/^https?:/, BACKEND.startsWith("https") ? "wss:" : "ws:");

/**
 * Live Session side panel (Phase G5).
 *
 * Owns the WebSocket lifecycle: opens on mount, closes on unmount. Exchanges
 * snapshot/participant/view/heart_burst/chat frames with the server. Bubbles
 * `onHeartBurst(photoId)` up to the viewer so the grid can animate.
 */
export default function LiveSessionPanel({
  slug, roomId, token, shareUrl, isHost, onClose, onHeartBurst, currentPhotoId,
  onParticipantsChange, onSendBound,
}) {
  const [connected, setConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [chat, setChat] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [draft, setDraft] = useState("");
  const [me, setMe] = useState(null);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const lastViewSent = useRef(null);

  // Connect
  useEffect(() => {
    if (!roomId || !token) return;
    const url = `${WS_BASE}/api/galleries/${slug}/live/rooms/${roomId}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Bind imperative sender for the parent to push heart_burst etc.
      if (typeof onSendBound === "function") {
        onSendBound((payload) => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
        });
      }
    };
    ws.onclose = () => { setConnected(false); };
    ws.onerror = () => { setConnected(false); };
    ws.onmessage = (evt) => {
      let msg; try { msg = JSON.parse(evt.data); } catch { return; }
      switch (msg.type) {
        case "snapshot":
          setParticipants(msg.participants || []);
          setChat(msg.chat || []);
          setTimeline(msg.recent_events || []);
          setMe(msg.your_participant_id);
          onParticipantsChange?.(msg.participants || [], msg.your_participant_id);
          break;
        case "participant_joined":
          setParticipants((p) => {
            const next = [...p.filter(x => x.participant_id !== msg.participant.participant_id), msg.participant];
            onParticipantsChange?.(next, me);
            return next;
          });
          break;
        case "participant_left":
          setParticipants((p) => {
            const next = p.filter(x => x.participant_id !== msg.participant_id);
            onParticipantsChange?.(next, me);
            return next;
          });
          break;
        case "view_changed":
          setParticipants((p) => {
            const next = p.map(x => x.participant_id === msg.participant_id ? { ...x, current_photo_id: msg.photo_id } : x);
            onParticipantsChange?.(next, me);
            return next;
          });
          break;
        case "heart_burst":
          onHeartBurst?.(msg.photo_id, msg.display_name);
          break;
        case "chat":
          setChat((c) => [...c, msg]);
          break;
        case "event":
          setTimeline((t) => {
            // De-dup chat_burst entries by collab — server updates in place
            const filtered = msg.entry.kind === "chat_burst"
              ? t.filter((e) => !(e.kind === "chat_burst" && e.collab_id === msg.entry.collab_id))
              : t;
            return [...filtered.slice(-19), msg.entry];
          });
          break;
        case "ping":
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "pong" }));
          break;
        case "rate_limited":
          toast.warning("Slow down — chat rate limit");
          break;
        default:
          break;
      }
    };

    return () => {
      try { ws.close(); } catch (e) { void e; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, token, slug]);

  // Push current_photo_id changes upstream (debounced via simple last-value compare)
  useEffect(() => {
    if (!currentPhotoId || !connected) return;
    if (lastViewSent.current === currentPhotoId) return;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "view", photo_id: currentPhotoId }));
      lastViewSent.current = currentPhotoId;
    }
  }, [currentPhotoId, connected]);

  // Scroll to chat bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.length]);

  function send() {
    const body = draft.trim();
    if (!body) return;
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "chat", body }));
    setDraft("");
  }

  function copyShare() {
    try { navigator.clipboard?.writeText(shareUrl); } catch (e) { void e; }
    toast.success("Share link copied");
  }

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 right-0 z-[85] h-full w-full sm:w-[380px] bg-[#FAF9F6] shadow-2xl flex flex-col"
        data-testid="live-session-panel"
      >
        <header className="px-5 py-4 border-b border-stone-200 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-[#C9A961]" />
              <h3 className="font-serif text-lg text-stone-900 font-light tracking-tight">Live session</h3>
              <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] ${connected ? "text-[#5F7A6F]" : "text-amber-700"}`} data-testid="live-conn-status">
                {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {connected ? "On air" : "Reconnecting…"}
              </span>
            </div>
            <button onClick={onClose} className="p-1 text-stone-500 hover:text-stone-900" aria-label="Close" data-testid="live-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
          {isHost && shareUrl && (
            <button onClick={copyShare} className="w-full text-left inline-flex items-center gap-2 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-[11px] uppercase tracking-[0.22em] text-stone-700" data-testid="live-copy-share">
              <Copy className="w-3 h-3" /> Copy share link
            </button>
          )}
        </header>

        <div className="px-5 py-3 border-b border-stone-200">
          <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-2">In the room · {participants.length}</div>
          <div className="flex flex-wrap gap-2" data-testid="live-participants">
            {participants.map((p) => (
              <div key={p.participant_id} className="flex items-center gap-1.5 bg-white border border-stone-200 px-2 py-1" data-testid={`live-participant-${p.participant_id}`}>
                <span className={`inline-flex items-center justify-center w-6 h-6 text-[10px] font-medium rounded-full ${p.role === "primary" ? "bg-[#C9A961] text-stone-900" : "bg-stone-200 text-stone-700"}`}>
                  {(p.display_name || "?").slice(0, 1).toUpperCase()}
                </span>
                <span className="text-xs text-stone-900">{p.display_name}</span>
                {p.participant_id === me && <span className="text-[9px] text-stone-400">· you</span>}
              </div>
            ))}
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-3 space-y-2" data-testid="live-chat-log">
          {chat.length === 0 && (
            <div className="py-10 text-center text-xs uppercase tracking-[0.22em] text-stone-400">No messages yet — say hi.</div>
          )}
          {chat.map((c, i) => (
            <div key={`${c.at}-${i}`} className="text-sm" data-testid="live-chat-msg">
              <div className="flex items-baseline gap-1.5 text-xs">
                <span className="font-medium text-stone-900">{c.display_name}</span>
                {c.role === "primary" && <span className="text-[9px] tracking-[0.22em] uppercase text-[#C9A961]">primary</span>}
              </div>
              <p className="text-sm text-stone-800 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-stone-200 px-5 py-3 bg-white flex flex-col gap-2">
          {/* G5.b — room timeline rail (last 5 events) */}
          {timeline.length > 0 && (
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400 -mt-1" data-testid="live-timeline">
              {timeline.slice(-5).reverse().map((ev) => {
                let line = "";
                if (ev.kind === "participant_joined") line = `${ev.display_name} joined`;
                else if (ev.kind === "participant_left") line = `${ev.display_name} left`;
                else if (ev.kind === "heart_burst") line = `${ev.display_name} hearted a photo`;
                else if (ev.kind === "chat_burst") line = `${ev.display_name} +${ev.count} chat${ev.count === 1 ? "" : "s"}`;
                else return null;
                const ago = (() => {
                  const t = new Date(ev.at).getTime();
                  const diff = Math.max(0, (Date.now() - t) / 1000);
                  if (diff < 60) return `${Math.floor(diff)}s ago`;
                  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                  return `${Math.floor(diff / 3600)}h ago`;
                })();
                return (
                  <div key={ev.id} className="flex items-center gap-2 normal-case tracking-normal text-[11px] text-stone-500" data-testid={`timeline-${ev.id}`}>
                    <span className="text-stone-900">{line}</span>
                    <span className="opacity-60">· {ago}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); send(); } }}
            placeholder="Say something…"
            rows={1}
            maxLength={500}
            className="flex-1 bg-transparent text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none resize-none"
            data-testid="live-chat-input"
          />
          <button
            type="submit"
            disabled={!draft.trim() || !connected}
            className="inline-flex items-center gap-1.5 px-3 h-8 bg-stone-900 text-[#F5EFE0] text-[10px] uppercase tracking-[0.22em] disabled:opacity-40 hover:opacity-90"
            data-testid="live-chat-send"
          >
            <Send className="w-3 h-3" />
          </button>
          </div>
        </form>
      </motion.aside>
    </AnimatePresence>
  );
}
